import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  RESPIRATORY_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Novavax COVID-19 Booster Study (50-64)
 * Target population: Healthy adults aged 50-64 (NOT at high risk)
 * Key: This study is for people WITHOUT chronic conditions
 */
export const novavaxBoosterScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49",
        disqualify: true
      },
      {
        value: "50_to_64",
        label: "50 to 64"
      },
      {
        value: "65_or_older",
        label: "65 or older",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study is specifically for adults aged 50 to 64.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    maxBMI: 30,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  {
    id: "chronic_health_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following chronic health conditions?",
    helpText: "This study is for generally healthy adults. Please select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "asthma",
        label: "Asthma",
        disqualify: true
      },
      {
        value: "copd",
        label: "COPD (Chronic Obstructive Pulmonary Disease)",
        disqualify: true
      },
      {
        value: "cystic_fibrosis",
        label: "Cystic fibrosis",
        disqualify: true
      },
      {
        value: "diabetes_type1",
        label: "Type 1 diabetes",
        disqualify: true
      },
      {
        value: "diabetes_type2",
        label: "Type 2 diabetes",
        disqualify: true
      },
      {
        value: "heart_failure",
        label: "Heart failure",
        disqualify: true
      },
      {
        value: "coronary_disease",
        label: "Coronary artery disease or history of heart attack",
        disqualify: true
      },
      {
        value: "stroke",
        label: "History of stroke or TIA (mini-stroke)",
        disqualify: true
      },
      {
        value: "kidney_disease",
        label: "Chronic kidney disease or dialysis",
        disqualify: true
      },
      {
        value: "hiv",
        label: "HIV",
        disqualify: true
      },
      {
        value: "immunodeficiency",
        label: "Immunodeficiency or weakened immune system",
        disqualify: true
      },
      {
        value: "blood_cancer",
        label: "Blood cancer (leukemia, lymphoma, multiple myeloma)",
        disqualify: true
      },
      {
        value: "organ_transplant",
        label: "Organ transplant (received or on waitlist)",
        disqualify: true
      },
      {
        value: "mental_health",
        label: "Schizophrenia, bipolar disorder, or current depression requiring treatment",
        disqualify: true
      },
      {
        value: "dementia_parkinsons",
        label: "Dementia or Parkinson's disease",
        disqualify: true
      },
      {
        value: "tuberculosis",
        label: "Active tuberculosis or current TB treatment",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above - I am generally healthy"
      },
    ],
    disqualifyMessage: "This study is specifically for generally healthy adults without chronic health conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        asthma: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true },
        ],
        copd: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true },
        ],
        cystic_fibrosis: [
          { attributeDefinitionId: 20354, booleanAttributeValue: true }, // has_CF
        ],
        diabetes_type1: [
          { attributeDefinitionId: 1356, booleanAttributeValue: true }, // has_diabetes_type1
        ],
        diabetes_type2: [
          { attributeDefinitionId: 702, booleanAttributeValue: true }, // has_diabetes_type2
        ],
        heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        coronary_disease: [
          { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease
        ],
        stroke: [
          { attributeDefinitionId: 1312, booleanAttributeValue: true }, // had_CVA
        ],
        kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
        hiv: [
          { attributeDefinitionId: 15025, booleanAttributeValue: true }, // has_HIV
        ],
        immunodeficiency: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
        ],
        blood_cancer: [
          { attributeDefinitionId: 23423, booleanAttributeValue: true }, // has_haematological_malignancy
        ],
        organ_transplant: [
          { attributeDefinitionId: 6300, booleanAttributeValue: true }, // has_organtransplant
        ],
        mental_health: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // placeholder for mental health conditions
        ],
        dementia_parkinsons: [
          { attributeDefinitionId: 2395, booleanAttributeValue: true }, // has_vasculardementia (covers dementia)
        ],
        tuberculosis: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // placeholder for TB
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "smoking_status",
    type: QuestionType.RADIO,
    question: "What is your smoking status?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "never",
        label: "I have never smoked"
      },
      {
        value: "quit_long_ago",
        label: "I quit smoking more than 6 months ago and smoked less than 10 pack-years total"
      },
      {
        value: "current_or_recent",
        label: "I currently smoke or quit within the past 6 months",
        disqualify: true
      },
      {
        value: "heavy_past_smoker",
        label: "I quit smoking but smoked 10+ pack-years total",
        disqualify: true
      },
    ],
    helpText: "Pack-years = (packs per day) × (years smoked). For example, smoking 1 pack/day for 10 years = 10 pack-years.",
    disqualifyMessage: "For safety reasons, current smokers or those with significant smoking history cannot participate in this study.",
    attributeMapping: mappers.value({
      never: [],
      quit_long_ago: [],
      current_or_recent: [],
      heavy_past_smoker: [],
    }),
  },

  {
    id: "covid_vaccine_history",
    type: QuestionType.RADIO,
    question: "Have you ever received a COVID-19 vaccine?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "never",
        label: "No, I have never received a COVID-19 vaccine"
      },
      {
        value: "yes_over_3_months",
        label: "Yes, but more than 3 months ago"
      },
      {
        value: "yes_within_3_months",
        label: "Yes, within the past 3 months",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received a COVID-19 vaccine in the past 3 months cannot participate.",
    attributeMapping: mappers.value({
      never: [
        { attributeDefinitionId: 21797, booleanAttributeValue: false }, // had_covid_vaccine
      ],
      yes_over_3_months: [
        { attributeDefinitionId: 21797, booleanAttributeValue: true },
        { attributeDefinitionId: 23730, booleanAttributeValue: false }, // had_covidvaccine_3months
      ],
      yes_within_3_months: [
        { attributeDefinitionId: 23730, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "recent_covid_infection",
    type: QuestionType.RADIO,
    question: "Have you had a confirmed COVID-19 infection in the past 3 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had a confirmed COVID-19 infection in the past 3 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had a confirmed COVID-19 infection in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have had a COVID-19 infection in the past 3 months cannot participate.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23428, booleanAttributeValue: true }, // had_covid_infection_or_vaccine_3_months
      ],
      no: [
        { attributeDefinitionId: 23428, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "vaccine_anaphylaxis",
    type: QuestionType.RADIO,
    question: "Have you ever had a severe allergic reaction (anaphylaxis) to any vaccine?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had anaphylaxis to a vaccine",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had anaphylaxis to a vaccine"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of anaphylaxis to vaccines cannot participate.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23486, booleanAttributeValue: true }, // had_anaphylaxis_vaccine
      ],
      no: [
        { attributeDefinitionId: 23486, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "heart_inflammation",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with myocarditis (heart muscle inflammation) or pericarditis (heart lining inflammation)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with myocarditis or pericarditis",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with myocarditis or pericarditis"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of myocarditis or pericarditis cannot participate.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 25888, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 25888, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_treatments",
    type: QuestionType.CHECKBOX,
    question: "Have you received any of the following in the past 3 months?",
    helpText: "Please select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "immunosuppressants",
        label: "Immunosuppressive medications",
        disqualify: true
      },
      {
        value: "blood_products",
        label: "Blood transfusions or blood products",
        disqualify: true
      },
      {
        value: "ivig",
        label: "IVIg (intravenous immunoglobulin) treatment",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received these treatments in the past 3 months cannot participate.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        immunosuppressants: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true },
        ],
        blood_products: [
          { attributeDefinitionId: 23724, booleanAttributeValue: true },
        ],
        ivig: [
          { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "pregnancy_breastfeeding",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or breastfeeding?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am currently pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not currently pregnant or breastfeeding"
      },
      {
        value: "not_applicable",
        label: "Not applicable (I am male or cannot become pregnant)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who are pregnant or breastfeeding cannot participate.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26453, booleanAttributeValue: true },
        { attributeDefinitionId: 26455, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 26453, booleanAttributeValue: false },
        { attributeDefinitionId: 26455, booleanAttributeValue: false },
      ],
      not_applicable: [],
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently participating in another clinical trial"
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "past_vaccine_study_participation",
    type: QuestionType.RADIO,
    question: "Have you ever participated in a vaccine study before?",
    helpText: "This information helps us understand your experience with vaccine research.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "participated",
        label: "Yes, I have participated in a vaccine study before"
      },
      {
        value: "screened_not_enrolled",
        label: "I have screened for a vaccine study but didn't enroll"
      },
      {
        value: "never_participated",
        label: "No, I have never participated in a vaccine study"
      },
    ],
    attributeMapping: mappers.value({
      participated: [],
      screened_not_enrolled: [],
      never_participated: [],
    }),
  },
];
